import React, { useCallback, useState, useMemo } from 'react';

import { AnalyticsListener } from '@atlaskit/analytics-next';
import { FabricChannel } from '@atlaskit/analytics-listeners';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import { TypeAheadAvailableNodes } from '@atlaskit/editor-common/type-ahead';
import type { ExtractPresetAPI } from '@atlaskit/editor-common/preset';
import type { useUniversalPreset } from '@atlaskit/editor-core/preset-universal';

import { useSkippableCoordination } from '@confluence/skippable-coordination-client';

import { EditorDatabasesOnboardingSpotlight } from './EditorDatabasesOnboardingSpotlight';

type PresetAPIType = ExtractPresetAPI<ReturnType<typeof useUniversalPreset>>;

export type EditorDatabasesOnboardingProps = {
	editorApi?: PresetAPIType;
	children: React.ReactNode;
};

export const showDatabaseQuickInsertAfterTable = (editorApi?: PresetAPIType, localId?: string) => {
	if (!editorApi || !editorApi.typeAhead || !editorApi.extension || !localId) {
		return;
	}

	const handler = editorApi.typeAhead.actions?.findHandlerByTrigger(
		TypeAheadAvailableNodes.QUICK_INSERT,
	);

	if (!handler) {
		return;
	}

	editorApi.extension.actions?.api()?.doc?.insertAfter(
		localId,
		{
			version: 1,
			type: 'doc',
			content: [
				{
					type: 'paragraph',
					content: [],
				},
			],
		},
		{ allowSelectionNearNewNode: true },
	);

	editorApi.typeAhead.actions?.open({
		triggerHandler: handler,
		query: 'database ', // space is important
		inputMethod: INPUT_METHOD.KEYBOARD,
	});

	// new local id
	return undefined;
};

export const EditorDatabasesOnboardingContext = React.createContext<{
	setLocalIdForOnboarding: (localId: string | undefined) => void;
}>({
	setLocalIdForOnboarding: () => {},
});

export const EditorDatabasesOnboardingImpl = ({
	editorApi,
	children,
}: EditorDatabasesOnboardingProps) => {
	const [localIdForOnboarding, setLocalIdForOnboarding] = useState<string | undefined>(undefined);

	const showOnboarding = localIdForOnboarding !== undefined;

	const [hasStartedEditorDatabasesOnboardingSpotlight, stopEditorDatabasesOnboardingSpotlight] =
		useSkippableCoordination('cc-databases-ga-editor-spotlight', !showOnboarding);

	const handleEvent = useCallback(
		(event: UIAnalyticsEvent) => {
			if (event.payload.action === 'inserted' && event.payload.actionSubjectId === 'table') {
				setLocalIdForOnboarding(event.payload.attributes.localId);
			}
		},
		[setLocalIdForOnboarding],
	);

	const onCreateDatabase = useCallback(async () => {
		await stopEditorDatabasesOnboardingSpotlight();
		setLocalIdForOnboarding((localId) => showDatabaseQuickInsertAfterTable(editorApi, localId));
	}, [editorApi, setLocalIdForOnboarding, stopEditorDatabasesOnboardingSpotlight]);

	const onDismiss = useCallback(async () => {
		await stopEditorDatabasesOnboardingSpotlight();
		setLocalIdForOnboarding(undefined);
	}, [stopEditorDatabasesOnboardingSpotlight, setLocalIdForOnboarding]);

	const onboardingContext = useMemo(() => ({ setLocalIdForOnboarding }), [setLocalIdForOnboarding]);

	return (
		<AnalyticsListener onEvent={handleEvent} channel={FabricChannel.editor}>
			{hasStartedEditorDatabasesOnboardingSpotlight && (
				<EditorDatabasesOnboardingSpotlight
					onCreateDatabase={onCreateDatabase}
					onDismiss={onDismiss}
				/>
			)}
			<EditorDatabasesOnboardingContext.Provider value={onboardingContext}>
				{children}
			</EditorDatabasesOnboardingContext.Provider>
		</AnalyticsListener>
	);
};

export const EditorDatabasesOnboarding = ({
	editorApi,
	children,
}: EditorDatabasesOnboardingProps) => {
	return (
		<EditorDatabasesOnboardingImpl editorApi={editorApi}>{children}</EditorDatabasesOnboardingImpl>
	);
};
